import config from '../config/environment';
import supabase from '../config/supabase';
import type {
  ProjectDashboardSnapshot,
  ProjectDashboardStudentRow,
} from '../types/projectDashboard';
import type { PaginatedResponse, PaginationInfo } from '../types/student';
import { handleServiceError } from '../utils/errorHandler';
import {
  mapProjectDashboardSnapshot,
  mapProjectDashboardStudentRow,
} from '../utils/projectDashboardSnapshot';

class ProjectDashboardServiceClass {
  async getSnapshot(projectCode: string): Promise<ProjectDashboardSnapshot> {
    const token = await this.getAccessToken();
    const url = new URL(`${config.supabaseFunctionsUrl}/project-dashboard-snapshot`);
    url.searchParams.set('projectCode', projectCode);

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: this.buildHeaders(token),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      handleServiceError(
        new Error((err as { error?: string })?.error || 'Erro ao carregar dashboard do projeto'),
        'carregar dashboard do projeto',
      );
    }

    const body = await response.json() as { data?: unknown };
    return mapProjectDashboardSnapshot(body.data);
  }

  async listStudents(
    projectCode: string,
    page: number = 1,
    pageSize: number = 10,
    search?: string,
  ): Promise<PaginatedResponse<ProjectDashboardStudentRow>> {
    const token = await this.getAccessToken();
    const url = new URL(`${config.supabaseFunctionsUrl}/project-dashboard-snapshot`);
    url.searchParams.set('projectCode', projectCode);
    url.searchParams.set('view', 'students');
    url.searchParams.set('page', String(page));
    url.searchParams.set('pageSize', String(pageSize));
    if (search?.trim()) {
      url.searchParams.set('search', search.trim());
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: this.buildHeaders(token),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      handleServiceError(
        new Error((err as { error?: string })?.error || 'Erro ao listar alunas do projeto'),
        'listar alunas do projeto',
      );
    }

    const body = await response.json() as {
      data?: unknown;
      pagination?: Partial<PaginationInfo>;
    };
    const pagination = body.pagination ?? {};
    const resolvedPageSize = Number(pagination.pageSize ?? pageSize);
    const resolvedTotalRecords = Number(pagination.totalRecords ?? 0);
    const resolvedTotalPages = Number.isFinite(Number(pagination.totalPages))
      ? Number(pagination.totalPages)
      : (resolvedTotalRecords === 0 ? 0 : Math.ceil(resolvedTotalRecords / resolvedPageSize));

    return {
      data: (Array.isArray(body.data) ? body.data : [])
        .map((row) => mapProjectDashboardStudentRow(row))
        .filter((row): row is ProjectDashboardStudentRow => row != null),
      pagination: {
        page: Number(pagination.page ?? page),
        pageSize: resolvedPageSize,
        totalRecords: resolvedTotalRecords,
        totalPages: resolvedTotalPages,
        hasNextPage: Boolean(pagination.hasNextPage),
        hasPreviousPage: Boolean(pagination.hasPreviousPage),
      },
    };
  }

  private async getAccessToken(): Promise<string | undefined> {
    const { data } = await supabase.auth.getSession();
    return data?.session?.access_token;
  }

  private buildHeaders(token: string | undefined): HeadersInit {
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      apikey: config.supabaseAnonKey,
    };
  }
}

export const ProjectDashboardService = new ProjectDashboardServiceClass();
